import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';

import {isEqual} from 'lodash';

import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  SubTitleSmall,
  ButtonTextSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {classify} from 'src/utils/classify';

import css from './filter-drop-down.css';


type MenuHeaderProps = {
  title: string;
  showBackIcon: boolean;
  handleGoBack?: () => void;
};

const MenuHeader = ({title, showBackIcon, handleGoBack}: MenuHeaderProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <div
      className={classify(css.filterHeader, {
        [css.filterHeaderWithIcon]: showBackIcon,
      })}
    >
      {showBackIcon && (
        <Button
          ariaLabel={t('Go_Back_Icon', 'Go Back Icon')}
          iconRightName="chevron-left"
          onClick={handleGoBack}
          size="small"
          type="ghost"
          className={classify(css.backIcon, {
            [css.backIconVisible]: showBackIcon,
          })}
        />
      )}
      <SubTitleSmall>{title}</SubTitleSmall>
    </div>
  );
};

type MenuFooterProps = {
  onSave?: () => Promise<unknown>;
  onCancel?: () => void;
  isSaving?: boolean;
};

const MenuFooter = ({onSave, onCancel, isSaving}: MenuFooterProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <div className={css.filterFooter}>
      <Button
        // @ts-expect-error - TS2769 - No overload matches this call.
        className={{wrapper: css.footerSecondaryCta}}
        size="small"
        type="ghost"
        onClick={onCancel}
      >
        {t('Cancel', 'Cancel')}
      </Button>
      <Button
        // @ts-expect-error - TS2769 - No overload matches this call.
        className={{wrapper: css.footerPrimaryCta}}
        size="small"
        type="secondary"
        onClick={onSave}
        isLoading={isSaving}
      >
        {t('Save', 'Save')}
      </Button>
    </div>
  );
};

type FilterDropDownProps = {
  headerTitle: string;
  menuOptions: Array<MenuOption>;
  selectedFilterKeys: Array<string>;
  handleOnSave: (keys: Array<string>) => Promise<unknown>;
  onSaveCallback: () => void;
  isEditingEntities?: boolean;
  setIsEditingEntities?: (flag: boolean) => void;
  isLoading?: boolean;
  isSavingTableFilters: boolean;
  handleFilterSave?: (keys: Array<string>) => void;
  handleCancel?: () => void;
  showEntityOptionCheck?: boolean;
  showSearch?: boolean;
};

const FilterDropDown = ({
  showSearch = false,
  headerTitle,
  menuOptions,
  selectedFilterKeys = [],
  handleOnSave,
  isEditingEntities = false,
  onSaveCallback,
  setIsEditingEntities = () => {},
  isLoading = false,
  isSavingTableFilters,
  handleFilterSave,
  handleCancel,
  showEntityOptionCheck = false,
}: FilterDropDownProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const clickAwayRef = React.useRef(null);
  const [selectedKeys, setSelectedKeys] = React.useState(selectedFilterKeys);

  const menuSelectedKeys = [
    ...selectedKeys,
    ...(showEntityOptionCheck ? ['entityVariable'] : []),
  ];

  // Avoid resetting state unless `selectedFilterKeys` has actually changed
  // TODO(Vivek): Need to come-up with a better state management
  React.useEffect(() => {
    if (!isEqual(selectedFilterKeys, selectedKeys)) {
      setSelectedKeys(selectedFilterKeys);
    }
  }, [selectedFilterKeys]);

  const getMenuOption = (option: MenuOption) => (
    <div
      className={css.menuOption}
      onClick={() => {
        if (option.key === 'entityVariable' && !option.disabled) {
          handleFilterSave?.(selectedKeys);
          setIsEditingEntities(true);
        }
      }}
    >
      <div
        className={classify({
          [css.menuOptionLabel]: option.key === 'entityVariable',
        })}
      >
        <ButtonTextSmall color="primary">
          <TruncatedTextWithTooltip
            tooltip={{bodyMaxLines: 10, delayMotionDuration: 'normal'}}
            line={3}
          >
            {option.label}
          </TruncatedTextWithTooltip>
        </ButtonTextSmall>
      </div>
      {option.key === 'entityVariable' && (
        <Button
          ariaLabel={t('Select_Filter_Entities', 'Select Filter Entities')}
          iconRightName="chevron-right"
          size="small"
          type="ghost"
          disabled={option.disabled}
        />
      )}
    </div>
  );

  const onCancel = () => {
    // @ts-expect-error - TS2339 - Property 'forceClose' does not exist on type 'never'.
    clickAwayRef.current?.forceClose();
  };

  const onGoBack = () => {
    handleFilterSave?.(selectedKeys);
    setIsEditingEntities(false);
  };

  const onSave = async () => {
    try {
      await handleOnSave(selectedKeys);
      handleFilterSave?.(selectedKeys);
      if (isEditingEntities) {
        setIsEditingEntities(false);
      }
      // @ts-expect-error - TS2339 - Property 'forceClose' does not exist on type 'never'.
      clickAwayRef.current?.forceClose();

      const successToast = (
        <Toast initialFocus={0} semantic={'success'}>
          <ToastTitle>{`${t(
            'Successfully_Saved_Columns',
            'Successfully Saved Columns',
          )}.`}</ToastTitle>
        </Toast>
      );
      toastApi.show(successToast);
      onSaveCallback();
    } catch (e: any) {
      const dangerToast = (
        <Toast initialFocus={0} semantic={'danger'}>
          <ToastTitle>{`${t(
            'Failed_Saving_Columns',
            'Failed Saving Columns',
          )}.`}</ToastTitle>
        </Toast>
      );
      toastApi.show(dangerToast);
    }
  };

  const handleChange = (option: MenuOption) => {
    if (option.key === 'entityVariable') {
      return;
    }
    setSelectedKeys((prevSelectedKeys) =>
      prevSelectedKeys.includes(option.key)
        ? prevSelectedKeys.filter((item) => item !== option.key)
        : [...prevSelectedKeys, option.key],
    );
  };

  return (
    <ButtonDropdown
      tooltip={{body: t('Edit_table_columns', 'Edit table columns')}}
      isLoading={isLoading}
      clickAwayRef={clickAwayRef}
      ariaLabel={t('Icon_Button_Dropdown', 'Icon Button Dropdown')}
      iconLeftName="table"
      size="small"
      type="ghost"
      anchorPosition="bottom-end"
      onOptionSelect={handleChange}
      menu={{
        size: 'medium',
        isFluid: false,
        staticLabels: {
          SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
          RESULTS: t('results', 'results'),
          RESULT: t('result', 'result'),
        },
        menuDisabled: isSavingTableFilters,
        options: menuOptions,
        allowSearch: isEditingEntities || showSearch,
        header: (
          <MenuHeader
            title={headerTitle}
            showBackIcon={isEditingEntities}
            handleGoBack={onGoBack}
          />
        ),
        footer: (
          <MenuFooter
            isSaving={isSavingTableFilters}
            onCancel={onCancel}
            onSave={onSave}
          />
        ),
        optionsVariant: 'checkbox',
        resolveLabel: getMenuOption,
        selectedKeys: menuSelectedKeys,
        virtualization: {
          enable: true,
        },
        allowWrap: true,
      }}
      onMenuClose={() => {
        setSelectedKeys(selectedFilterKeys);
        handleCancel?.();
      }}
    />
  );
};

export default FilterDropDown;
